import logger from '../../../utils/logger';
// Utilities for handling training comps logic in CaseReview

export const getTrainingUniquePids = (caseObject) => {
  try {
    const set = new Set();
    const trainingComps = caseObject?.TrainingComps || [];
    if (!Array.isArray(trainingComps)) return [];
    trainingComps.forEach((entry) => {
      const comps = entry?.Comps || {};
      Object.values(comps).forEach((pid) => pid && set.add(pid));
    });
    return Array.from(set);
  } catch (_e) {
    return [];
  }
};

export const isSelectionEqualToTraining = (selection, trainingUniquePids) => {
  try {
    if (!Array.isArray(selection)) return false;
    if (selection.length !== trainingUniquePids.length) return false;
    const setSel = new Set(selection);
    for (const pid of trainingUniquePids) {
      if (!setSel.has(pid)) return false;
    }
    return true;
  } catch (_e) {
    return false;
  }
};

/**
 * Extract training user information from cases
 * 
 * @param {Array} cases - Array of case objects
 * @param {Function} getUserNameById - Function to get user name by ID
 * @returns {Object} - Object with count, userNames, and userIds arrays
 */
export const getTrainingUsersInfo = (cases, getUserNameById) => {
  try {
    const userIds = new Set();
    
    cases.forEach((c) => {
      const tc = c?.TrainingComps;
      if (Array.isArray(tc) && tc.length > 0) {
        tc.forEach((entry) => {
          const userId = entry?.UserID;
          if (userId) {
            userIds.add(userId);
          }
        });
      }
    });
    
    const uniqueUserIds = Array.from(userIds);
    const userNames = uniqueUserIds.map(userId => getUserNameById(userId));
    
    return {
      count: uniqueUserIds.length,
      userNames,
      userIds: uniqueUserIds
    };
  } catch (error) {
    logger.error('Error extracting training users info:', error);
    return { count: 0, userNames: [], userIds: [] };
  }
};

/**
 * Group training comps by case (parcel_id), optionally filtered by a specific user ID
 * 
 * @param {Array} cases - Array of case objects
 * @param {number|null} userId - Optional user ID to filter by. If provided, only returns comps from that user
 * @returns {Object} - Training comps grouped by parcel_id
 */
export const groupTrainingCompsByCaseFiltered = (cases, userId = null) => {
  const grouped = {};
  
  try {
    cases.forEach((c) => {
      const parcelId = c?.parcel_id;
      if (!parcelId) return;
      
      const tc = c?.TrainingComps;
      if (!Array.isArray(tc) || tc.length === 0) return;
      
      // Collect all training comp entries for this case
      const mergedComps = {};
      let repId = null;
      
      tc.forEach((entry) => {
        // Filter by userId if provided
        if (userId && entry?.UserID !== userId) return;
        
        const comps = entry?.Comps || {};
        // Merge comps, keeping the first occurrence of each index
        Object.entries(comps).forEach(([idx, pid]) => {
          if (pid && !mergedComps[idx]) {
            mergedComps[idx] = pid;
          }
        });
        
        // Use RepId from first entry if available
        if (!repId && entry?.RepId) {
          repId = entry.RepId;
        }
      });
      
      if (Object.keys(mergedComps).length > 0) {
        grouped[parcelId] = {
          Comps: mergedComps,
          RepId: repId || c?.RepID?.[0] || c?.RepId || null
        };
      }
    });
  } catch (error) {
    logger.error('Error grouping training comps by case:', error);
  }
  
  return grouped;
};


